import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Search, FileText, Video, Download, HelpCircle, Filter, ArrowRight } from "lucide-react";
import SectionHeading from "../components/SectionHeading";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = ["All", "Veteran Disability", "Insurance", "Education"];
const TYPES = ["All", "Article", "FAQ", "Video", "Download"];

const typeIcon = {
  Article: FileText,
  FAQ: HelpCircle,
  Video: Video,
  Download: Download,
};

const ResourcesPage = () => {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("All");
  const [type, setType] = useState("All");

  useEffect(() => {
    axios
      .get(`${API}/resources`, { params: { published_only: true } })
      .then((res) => setResources(res.data))
      .catch((err) => console.error("Failed to load resources:", err))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return resources.filter((r) => {
      if (category !== "All" && r.category !== category) return false;
      if (type !== "All" && r.type !== type) return false;
      if (q && !`${r.title} ${r.excerpt}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [resources, query, category, type]);

  return (
    <div className="pt-32 pb-24 bg-white min-h-screen">
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <SectionHeading
          eyebrow="Resources"
          title="Learn, compare, and prepare."
          subtitle="Articles, FAQs, videos, and downloads across our three practices. New content added regularly."
          align="center"
        />

        {/* Search + filters */}
        <div className="bg-slate-50 p-5 md:p-6 mb-10 border border-slate-200">
          <div className="flex items-center gap-3 bg-white border border-slate-200 px-4 py-3 mb-4">
            <Search size={18} className="text-slate-400" />
            <input
              type="text"
              placeholder="Search resources..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full outline-none bg-transparent text-slate-700"
              data-testid="resources-search"
            />
          </div>
          <div className="flex flex-col md:flex-row gap-3">
            <FilterChips label="Category" value={category} onChange={setCategory} options={CATEGORIES} testId="category" />
            <FilterChips label="Type" value={type} onChange={setType} options={TYPES} testId="type" />
          </div>
        </div>

        {loading ? (
          <div className="text-center py-16 text-slate-500">Loading resources…</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-slate-500">
            <Filter className="mx-auto mb-3 text-slate-300" size={36} />
            {resources.length === 0
              ? "Resources are being added — check back soon."
              : "No resources match your filters."}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((r, idx) => {
              const Icon = typeIcon[r.type] || FileText;
              return (
                <motion.div
                  key={r.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: idx * 0.04 }}
                  className="p-6 border border-slate-200 hover:border-[#C89B3C] hover:shadow-lg transition-all bg-white flex flex-col"
                  data-testid={`resource-card-${r.slug}`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-xs font-medium tracking-wider uppercase text-slate-500">
                      {r.category}
                    </span>
                    <Icon className="text-[#C89B3C]" size={18} />
                  </div>
                  <h3 className="font-serif text-lg font-bold text-slate-900 mb-2 leading-snug">
                    {r.title}
                  </h3>
                  <p className="text-sm text-slate-600 leading-relaxed flex-1">{r.excerpt}</p>
                  <Link
                    to={`/resources/${r.slug}`}
                    className="mt-4 pt-4 border-t border-slate-100 inline-flex items-center gap-2 text-[#0B2343] text-sm font-medium hover:text-[#C89B3C] transition-colors"
                    data-testid={`resource-link-${r.slug}`}
                  >
                    Read more <ArrowRight size={14} />
                  </Link>
                </motion.div>
              );
            })}
          </div>
        )}

        <div className="mt-16 text-center bg-slate-50 p-8 border-l-4 border-[#C89B3C]">
          <p className="text-slate-700 text-sm">
            Have a topic you'd like us to cover?{" "}
            <a href="mailto:info@noblestrategiesconsulting.com" className="text-[#C89B3C] font-medium hover:underline">
              Let us know
            </a>
            .
          </p>
        </div>
      </div>
    </div>
  );
};

const FilterChips = ({ label, value, onChange, options, testId }) => (
  <div className="flex flex-wrap items-center gap-2">
    <span className="text-xs font-medium text-slate-500 uppercase tracking-wider mr-2">
      {label}:
    </span>
    {options.map((opt) => (
      <button
        key={opt}
        onClick={() => onChange(opt)}
        data-testid={`resources-filter-${testId}-${opt.toLowerCase().replace(/ /g, "-")}`}
        className={`text-xs px-3 py-1.5 border transition-all ${
          value === opt
            ? "bg-[#0B2343] text-white border-[#0B2343]"
            : "bg-white text-slate-600 border-slate-300 hover:border-slate-500"
        }`}
      >
        {opt}
      </button>
    ))}
  </div>
);

export default ResourcesPage;
